import PropTypes from 'prop-types';
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";

// Soft UI Components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftAvatar from "components/SoftAvatar";
import { DASHBOARD, sectionHeaderGradient } from "../../colors";

function ReportDetails({ 
  reportData, 
  isMobile, 
  apiClient, 
  getIncidentTypeLabel = (v) => v,
  getInjuryTypeLabel = (v) => v,
  getResponsibleEntityLabel = (v) => v,
  getIncidentPlaceTypeLabel = (v) => v,
  getForeignSubmissionResponsibleLabel = (v) => v
}) {
  const formatDate = (raw) => {
    if (!raw) return "—";
    try {
      return new Intl.DateTimeFormat("ro-RO", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(raw));
    } catch {
      return raw;
    }
  };

  const handleDocumentDownload = async (file) => {
    try {
      if (apiClient && file.file) {
        // Use authenticated API call to get the document
        const response = await apiClient.getDocument(file.file);
        
        // Get content type from headers with fallback
        const contentType = response.headers?.['content-type'] || 
                           response.headers?.['Content-Type'] || 
                           'application/octet-stream';
        
        // Create blob from response data
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        
        // Create download link
        const link = document.createElement('a');
        link.href = url;
        link.download = file.filename || decodeURIComponent(file.file.split("/").pop()) || "document";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the URL object
        window.URL.revokeObjectURL(url);
      } else {
        // Fallback to direct link if no API client
        window.open(file.file, '_blank');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      // Fallback to direct link on error
      window.open(file.file, '_blank');
    }
  };

  return (
    <SoftBox p={2}>
      <Grid container spacing={2}>
        {/* Informații generale */}
        <Grid item xs={12} md={8}>
          <SoftBox 
            height="100%"
            border="1px solid #e0e0e0"
            borderRadius="lg" 
            overflow="hidden"
            sx={{ display: "flex", flexDirection: "column" }}
          >
            {/* Header */}
            <SoftBox
              px={2}
              py={1.25}
              sx={{
                background: sectionHeaderGradient,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ color: "rgba(255,255,255,0.85)", fontSize: "1rem !important" }}>
                assignment
              </Icon>
              <SoftTypography
                variant="caption"
                color="white"
                sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", fontSize: "0.65rem", color: "#ffffff !important" }}
              >
              Informații generale
            </SoftTypography>
            </SoftBox>

            {/* Câmpuri */}
            <SoftBox sx={{ flex: 1, bgcolor: "white" }}>
              <Grid container>
                {[
                  { icon: "tag",              label: "Cod sesizare",           value: reportData.uuid },
                  { icon: "calendar_today",   label: "Data depunerii",          value: formatDate(reportData.created_at) },
                  { icon: "account_balance",  label: "Instituție curentă",      value: reportData.institution_entity },
                  { icon: "category",         label: "Tip incident",            value: getIncidentTypeLabel(reportData.incident_type) },
                  { icon: "healing",          label: "Vătămare",                value: getInjuryTypeLabel(reportData.injury_type) },
                  { icon: "gavel",            label: "Persoană reclamată",      value: getResponsibleEntityLabel(reportData.responsible) },
                  { icon: "place",            label: "Locul faptei",            value: reportData.deed_location },
                  { icon: "location_on",      label: "Loc incident",            value: getIncidentPlaceTypeLabel(reportData.incident_place_type) },
                  { icon: "history",          label: "Sesizare depusă anterior", value: reportData.foreign_submission === "yes" ? "Da" : reportData.foreign_submission === "no" ? "Nu" : "—" },
                  ...(reportData.foreign_submission === "yes"
                    ? [{ icon: "send", label: "Sesizată anterior la", value: getForeignSubmissionResponsibleLabel(reportData.foreign_submission_responsible) }]
                    : []),
                  { icon: "shield",           label: "Confidențialitate",       value: reportData.confidential_data === true ? "Da" : reportData.confidential_data === false ? "Nu" : "—" },
                ].map(({ icon, label, value }, idx, arr) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <SoftBox
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.25,
                        px: 2,
                        py: 1.1,
                        borderBottom: idx < arr.length - 1 ? "1px solid #f5f5f5" : "none",
                        borderRight: idx % 2 === 0 ? "1px solid #f5f5f5" : "none",
                        bgcolor: idx % 4 < 2 ? "white" : "#fafbff",
                      }}
                    >
                      <SoftBox
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "7px",
                          bgcolor: "#e8f0fe",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: "2px",
                        }}
                      >
                        <Icon sx={{ fontSize: "0.85rem !important", color: DASHBOARD.blue }}>{icon}</Icon>
                      </SoftBox>
                      <SoftBox minWidth={0}>
                        <SoftTypography
                          variant="caption"
                          sx={{ color: "#8392ab", fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600, display: "block" }}
                        >
                    {label}
                  </SoftTypography>
                        <SoftTypography
                          variant="caption"
                          sx={{ color: "#344767", fontWeight: 500, fontSize: "0.78rem", wordBreak: "break-word", display: "block" }}
                        >
                          {value || "—"}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                </Grid>
              ))}
            </Grid>
            </SoftBox>
          </SoftBox>
        </Grid>

        {/* Date petent — always visible in admin */}
          <Grid item xs={12} md={4}>
            <SoftBox 
            height="100%"
            border={reportData.confidential_data ? "1px solid #e0a800" : "1px solid #e0e0e0"}
              borderRadius="lg" 
            overflow="hidden"
            sx={{ display: "flex", flexDirection: "column" }}
          >
            {/* Header */}
            <SoftBox
              px={2}
              py={1.25}
              sx={{
                background: reportData.confidential_data
                  ? "linear-gradient(135deg, #b45309, #d97706)"
                  : sectionHeaderGradient,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <SoftBox display="flex" alignItems="center" gap={1}>
                <Icon sx={{ color: "rgba(255,255,255,0.85)", fontSize: "1rem !important" }}>
                  person
                </Icon>
                <SoftTypography
                  variant="caption"
                  color="white"
                  sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", fontSize: "0.65rem", color: "#ffffff !important" }}
                >
                Date petent
              </SoftTypography>
              </SoftBox>
              {reportData.confidential_data && (
                <SoftBox display="flex" alignItems="center" gap={0.5}>
                  <Icon sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.85rem !important" }}>lock</Icon>
                  <SoftTypography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 600, fontSize: "0.6rem", letterSpacing: "0.5px" }}>
                    CONFIDENȚIAL
                  </SoftTypography>
                </SoftBox>
              )}
            </SoftBox>

            {/* Avatar + Nume */}
            <SoftBox
              px={2}
              py={2}
              display="flex"
              alignItems="center"
              gap={1.5}
              sx={{ borderBottom: "1px solid #f0f0f0", bgcolor: reportData.confidential_data ? "#fffbf0" : "#f8faff" }}
            >
                <SoftAvatar 
                  name={reportData.name} 
                  size="md" 
                  variant="rounded" 
                  bgColor="info" 
                sx={{ flexShrink: 0 }}
                />
                <SoftBox display="flex" flexDirection="column">
                <SoftTypography variant="body2" fontWeight="bold" sx={{ color: DASHBOARD.title, lineHeight: 1.2, mb: 0 }}>
                  {reportData.name || "—"}
                </SoftTypography>
                <SoftTypography variant="caption" sx={{ color: "#8392ab", lineHeight: 1.2 }}>
                  Petent
                </SoftTypography>
              </SoftBox>
            </SoftBox>

            {/* Câmpuri detaliate */}
            <SoftBox px={2} py={1.5} sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.25, bgcolor: "white" }}>
              {[
                { icon: "email",      label: "Adresă e-mail",   value: reportData.email },
                { icon: "phone",      label: "Telefon",          value: reportData.phone },
                { icon: "cake",       label: "Data nașterii",    value: reportData.date_of_birth },
                { icon: "home",       label: "Adresă fizică",    value: reportData.address },
              ].map(({ icon, label, value }) => (
                <SoftBox key={label} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, py: 0.75, borderBottom: "1px solid #f5f5f5" }}>
                  <SoftBox
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "7px",
                      bgcolor: "#e8f0fe",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: "0.9rem !important", color: DASHBOARD.blue }}>{icon}</Icon>
                  </SoftBox>
                  <SoftBox display="flex" flexDirection="column">
                    <SoftTypography variant="caption" sx={{ color: "#8392ab", fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600, display: "block" }}>
                      {label}
                    </SoftTypography>
                    <SoftTypography variant="caption" sx={{ color: "#344767", fontWeight: 500, fontSize: "0.78rem", wordBreak: "break-word" }}>
                      {value || "—"}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
              ))}
            </SoftBox>
            </SoftBox>
          </Grid>

        {/* Descriere sesizare */}
        <Grid item xs={12}>
          <SoftBox 
            p={2} 
            border="1px solid #eee" 
            borderRadius="lg" 
            bgcolor="#f8f9fa"
          >
            <SoftTypography variant="subtitle2" fontWeight="medium" mb={1}>
              Descrierea detaliată a sesizării
            </SoftTypography>
            <SoftTypography variant="body2">
              {reportData.description}
            </SoftTypography>
          </SoftBox>
        </Grid>

        {/* Atașamente */}
        <Grid item xs={12}>
          <SoftBox 
            p={2} 
            border="1px solid #eee" 
            borderRadius="lg" 
            bgcolor="#f8f9fa"
          >
            <SoftTypography variant="subtitle2" fontWeight="medium" mb={1}>
              Atașamente primite
            </SoftTypography>
            {reportData.files && reportData.files.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {reportData.files.map((file, index) => {
                  // Extract file name from URL
                  const fileName = file.file ? decodeURIComponent(file.file.split("/").pop()) : "Document";
                  // Determine icon by extension
                  const ext = fileName.split('.').pop().toLowerCase();
                  let icon = "description";
                  if (["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"].includes(ext)) icon = "image";
                  else if (["pdf"].includes(ext)) icon = "picture_as_pdf";
                  else if (["doc", "docx"].includes(ext)) icon = "article";
                  else if (["xls", "xlsx"].includes(ext)) icon = "table_chart";
                  else if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) icon = "archive";
                  return (
                    <Chip
                      key={file.id || index}
                      icon={<Icon fontSize="small" sx={{ color: '#1a1a1a !important' }}>{icon}</Icon>}
                      label={fileName}
                      clickable
                      variant="filled"
                      size="medium"
                      onClick={() => handleDocumentDownload(file)}
                      sx={{ 
                        m: 0.5,
                        bgcolor: '#f5f5f5',
                        color: '#1a1a1a',
                        fontWeight: 500,
                        border: '1px solid #e0e0e0',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: '#ffffff',
                          borderColor: '#bdbdbd',
                        },
                        '& .MuiChip-icon': {
                          color: '#1a1a1a',
                        }
                      }}
                    />
                  );
                })}
              </Stack>
            ) : (
              <SoftTypography variant="body2" sx={{ color: '#b0b0b0' }}>
                Nu au fost încărcate documente de către petent.
              </SoftTypography>
            )}
          </SoftBox>
        </Grid>
      </Grid>
    </SoftBox>
  );
}

ReportDetails.propTypes = {
  reportData: PropTypes.shape({
    uuid: PropTypes.string, // Add uuid for prop validation
    id: PropTypes.string,
    created_at: PropTypes.string,
    institution_entity: PropTypes.string,
    incident_type: PropTypes.string,
    injury_type: PropTypes.string,
    responsible: PropTypes.string,
    deed_location: PropTypes.string,
    incident_place_type: PropTypes.string,
    foreign_submission: PropTypes.string,
    foreign_submission_responsible: PropTypes.string,
    confidential_data: PropTypes.bool,
    anonymous: PropTypes.string,
    petitioner: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
    }),
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    date_of_birth: PropTypes.string,
    address: PropTypes.string,
    description: PropTypes.string,
    files: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string,
        file: PropTypes.string,
        filename: PropTypes.string,
      })
    ),
  }).isRequired,
  isMobile: PropTypes.bool.isRequired,
  apiClient: PropTypes.object,
  getIncidentTypeLabel: PropTypes.func,
  getInjuryTypeLabel: PropTypes.func,
  getResponsibleEntityLabel: PropTypes.func,
  getIncidentPlaceTypeLabel: PropTypes.func,
  getForeignSubmissionResponsibleLabel: PropTypes.func,
};

export default ReportDetails;
